import { reactive } from 'vue';
import { api, ApiError } from './http.js';

/* Офлайн-очередь цеховых действий.

   Wi-Fi у станков рвётся, а нажатие «сдал 12 годных» терять нельзя. Каждое
   действие складывается в localStorage и отправляется, пока не будет принято.
   Сервер дедуплицирует по clientRef, поэтому повтор безопасен (см. README,
   раздел про идемпотентность).

   Очередь СТРОГО ПОСЛЕДОВАТЕЛЬНАЯ. Отправлять параллельно нельзя: «старт
   операции» и «сдать 5 годных» связаны порядком — если старт не прошёл,
   отчёт упрётся в незапущенную операцию. Поэтому одна задача за раз, и при
   неустранимой ошибке очередь ВСТАЁТ, а не проглатывает остальные действия:
   оператор должен увидеть проблему, а не потерять пять нажатий. */

const KEY = 'mes-queue';
const MAX_ATTEMPTS = 8;

export const queueState = reactive({
  items: [],
  sending: false,
  online: navigator.onLine,
  /* Задача, на которой очередь остановилась. Пока она не разрешена,
     остальные не отправляются. */
  blocked: null,
  lastError: null,
  lastSentAt: null,
});

const load = () => {
  try { return JSON.parse(localStorage.getItem(KEY) || '[]'); } catch { return []; }
};
const persist = () => {
  try { localStorage.setItem(KEY, JSON.stringify(queueState.items)); } catch { /* переполнение или приватный режим */ }
};

queueState.items = load();

/* Идентификатор отправки. crypto.randomUUID есть во всех браузерах,
   которые нас интересуют, но на http:// без TLS его может не быть —
   поэтому есть запасной вариант. */
const newRef = () => {
  try { return crypto.randomUUID(); } catch {
    return 'r-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }
};

/* Кто и на каком станке сделал нажатие.

   Записывается в момент нажатия, а не отправки. Очередь переживает выход
   оператора и перезагрузку планшета, а сервер берёт исполнителя и станок
   из токена отправителя. Без этой отметки пересменка при плохой связи
   отправляла бы выпуск Иванова под именем Петрова и на его станок:
   учёт разъезжается молча, и найти это потом невозможно. */
function currentActor() {
  try {
    const raw = localStorage.getItem('mes-token');
    if (!raw) return null;
    const p = JSON.parse(atob(raw.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return { user: p.sub || null, equipment: p.equipment || null };
  } catch { return null; }
}

/* Поставить действие в очередь. Возвращает clientRef, по которому можно
   отследить судьбу задачи. */
export function enqueue({ label, method = 'post', path, body = {} }) {
  const actor = currentActor();
  const task = {
    clientRef: body.clientRef || newRef(),
    label,
    method,
    path,
    body,
    actor,
    createdAt: new Date().toISOString(),
    attempts: 0,
    error: null,
  };
  task.body.clientRef = task.clientRef;

  queueState.items.push(task);
  persist();
  void flush();
  return task.clientRef;
}

/* Совпадает ли текущая сессия с той, в которой нажали кнопку.

   Задачи без отметки (оставшиеся от прошлой версии) пропускаем: они уже
   лежат в localStorage у людей, и отвергнуть их значит потерять учёт
   ради чистоты правила. */
function actorMatches(task) {
  if (!task.actor) return true;
  const now = currentActor();
  if (!now) return false;
  return String(task.actor.user) === String(now.user)
    && String(task.actor.equipment || '') === String(now.equipment || '');
}

/* Что это было: сервер группирует уведомления по виду действия */
function видДействия(task) {
  if (task.path === '/production/calls') return 'call';
  if (/\/production\/operations\/[a-f0-9]{24}\/status/i.test(task.path)) return 'operation-status';
  return 'other';
}

/* Чужие управляющие действия — мастеру, а не в исполнение.

   Отчёт о выпуске — факт, детали лежат на столе, и сервер обязан о них
   узнать. Пауза и завершение — команда о состоянии, и состояние ушло вперёд:
   выполнить их сейчас значит закрыть операцию, над которой уже работает
   сменщик, и записать это на него. Поэтому отдаём как уведомление:
   мастер смотрит на живую картину и решает сам. */
async function сдатьМастеру() {
  const чужие = queueState.items.filter((t) => !actorMatches(t));
  if (!чужие.length) return;

  let отдано = 0;
  for (const task of чужие) {
    try {
      await api.post('/production/intents', {
        kind: видДействия(task),
        label: task.label,
        path: task.path,
        payload: task.body,
        at: task.createdAt,
        clientRef: task.clientRef,
        claimedOperator: task.actor?.user || null,
        claimedEquipment: task.actor?.equipment || null,
      });
      const i = queueState.items.findIndex((t) => t.clientRef === task.clientRef);
      if (i !== -1) queueState.items.splice(i, 1);
      отдано += 1;
    } catch (e) {
      /* Не отдалось — оставляем в очереди и молчим: это уведомление,
         а не учётная запись, и ронять из-за него экран оператора нельзя. */
      if (e instanceof ApiError && e.offline) break;
    }
  }

  if (отдано) {
    queueState.lastError = null;
    persist();
    emit('handed-over', { count: отдано });
  }
}

let flushing = false;

/* Отправка. Обрабатывает по одной задаче с головы очереди. */
export async function flush() {
  if (flushing || queueState.blocked) return;
  if (!queueState.items.length) return;
  if (!navigator.onLine) { queueState.online = false; return; }
  /* Без сессии не отправляем и не блокируем: на экране входа полосы
     состояния нет, и очередь встала бы невидимо. Отправка начнётся,
     когда кто-нибудь войдёт. */
  if (!currentActor()) return;

  flushing = true;
  queueState.sending = true;

  try {
    while (queueState.items.length && !queueState.blocked) {
      /* Какую задачу берём.

         Порядок важен ВНУТРИ одного человека: «старт операции» должен уйти
         раньше «сдал 5 годных». Между разными людьми порядка нет — это
         независимые записи, и чужая в голове очереди не повод стоять.

         Поэтому берём первую отправляемую: свою — любую; чужую — только
         отчёт о выпуске, у него есть честный путь через `orphan`. Чужие
         «пауза» и «вызов» пропускаем: приписать их текущему оператору
         значит соврать, а держать из-за них терминал — остановить цех.
         Они дождутся своего хозяина либо мастера на закрытии смены. */
      const idx = queueState.items.findIndex(
        (t) => actorMatches(t) || t.path === '/production/reports'
      );

      /* Своих задач и чужого выпуска не осталось — значит впереди только
         чужие управляющие действия. Их не выполняют (пауза трёхчасовой
         давности закрыла бы операцию, над которой уже работают), но и
         держать в планшете нельзя: там их никто не увидит. Выгружаем
         мастеру списком и очищаем очередь. */
      if (idx === -1) {
        await сдатьМастеру();
        break;
      }
      const task = queueState.items[idx];

      /* Чужое нажатие: отправляем КОЛИЧЕСТВО, но не личность.

         Раньше очередь тут вставала, и это было хуже, чем кажется. Пока
         запись Иванова лежала в планшете, сервер не знал о его деталях —
         показывал сменщику остаток, которого физически нет. Сменщик доделывал
         «недостающее», цех выпускал лишнее, а детали Иванова потом отвергала
         защита от сверхвыпуска. Терминал при этом стоял: одна чужая запись
         в голове блокировала всю очередь, включая работу сменщика.

         Поэтому отправляем сразу — остаток становится честным, — но помечаем
         `orphan`. Сервер примет количество и пометит принадлежность
         неподтверждённой; кто это сделал, решит мастер. Записать чужую работу
         на текущего оператора нельзя: это подлог, и сервер на это не пойдёт. */
      const своё = actorMatches(task);

      task.attempts += 1;

      try {
        const тело = своё ? task.body : {
          ...task.body,
          orphan: true,
          claimedOperator: task.actor?.user || null,
          claimedEquipment: task.actor?.equipment || null,
          ts: task.createdAt,
        };
        const res = await api[task.method](task.path, тело);

        // Убираем именно отправленную: она не обязательно в голове очереди
        queueState.items.splice(idx, 1);
        task.error = null;
        queueState.lastError = null;
        queueState.lastSentAt = new Date().toISOString();
        /* Успешная отправка — это и есть доказательство связи. Без этой строки
           признак, снятый одной сетевой ошибкой, не возвращался: событие online
           браузер шлёт только при смене состояния адаптера, а обрыв бывает
           и выше — точка доступа жива, сервер недоступен. Полоса продолжала
           говорить «Нет связи», когда всё уже отправлялось, и оператор
           переставал ей верить. */
        queueState.online = true;
        persist();

        emit('sent', { task, response: res, replay: !!res?.__replay });
      } catch (e) {
        const isApi = e instanceof ApiError;
        task.error = e.message;

        if (isApi && e.offline) {
          // Связь пропала — не тратим попытки, ждём события online
          task.attempts -= 1;
          queueState.online = false;
          queueState.lastError = 'Нет связи. Отправка возобновится автоматически';
          persist();
          break;
        }

        if (isApi && e.retryable && task.attempts < MAX_ATTEMPTS) {
          /* 409 «ещё выполняется» и 5xx лечатся повтором. Растущая пауза,
             чтобы не долбить сервер в цикле. */
          queueState.lastError = `${task.label}: ${e.message}`;
          persist();
          await new Promise((r) => setTimeout(r, Math.min(1000 * 2 ** (task.attempts - 1), 15000)));
          continue;
        }

        /* Неустранимая ошибка: сверхвыпуск, закрытая операция, нет прав.
           Повтор не поможет — останавливаем очередь и показываем оператору.

           Код ответа запоминаем: остановка из-за истёкшей сессии снимается
           сама при следующем входе, а «превышение выпуска» — нет. */
        queueState.blocked = { ...task, status: isApi ? e.status : null };
        queueState.lastError = `${task.label}: ${e.message}`;
        persist();
        emit('blocked', { task, error: e });
        break;
      }
    }
  } finally {
    flushing = false;
    queueState.sending = false;
  }
}

/* Снять блокировку: оператор согласился отбросить проблемное действие
   либо исправил условия и хочет повторить. */
export function resolveBlocked({ drop = false } = {}) {
  if (!queueState.blocked) return;

  /* Ищем по ключу отправки, а не по голове очереди: встать могла запись
     из середины — свои и чужие теперь отправляются вперемешку. */
  const i = queueState.items.findIndex((t) => t.clientRef === queueState.blocked.clientRef);
  if (i !== -1) {
    if (drop) queueState.items.splice(i, 1);
    else queueState.items[i].attempts = 0;
  }

  queueState.blocked = null;
  queueState.lastError = null;
  persist();
  void flush();
}

/* Вход выполнен — снимаем остановку, вызванную истёкшей сессией.

   Токен кончается посреди смены, очередь упирается в 401 и встаёт. Человек
   входит заново и первым делом видит красную полосу с ошибкой авторизации,
   которой уже нет: успешный вход выглядит как сбой, а нажатие «Повторить»
   приходится объяснять.

   Снимаем только остановку по 401. «Превышение выпуска» и «операция закрыта»
   входом не лечатся, и гасить их значило бы прятать от человека то, что он
   должен разобрать. Проверка «своё ли это нажатие» остаётся на месте:
   если вошёл другой оператор, очередь встанет снова, уже по своей причине. */
export function resumeAfterLogin() {
  if (queueState.blocked?.status === 401) {
    queueState.blocked = null;
    queueState.lastError = null;
    const head = queueState.items[0];
    if (head) head.attempts = 0;
    persist();
  }
  void flush();
}

/* Полная очистка — на случай, когда очередь безнадёжно расходится с сервером.
   Вызывается только по явному решению человека. */
export function clearQueue() {
  queueState.items = [];
  queueState.blocked = null;
  queueState.lastError = null;
  persist();
}

// --- простые события, чтобы экраны могли обновлять свои данные ---
const listeners = new Map();
export const onQueue = (event, fn) => {
  if (!listeners.has(event)) listeners.set(event, new Set());
  listeners.get(event).add(fn);
  return () => listeners.get(event)?.delete(fn);
};
const emit = (event, payload) => {
  for (const fn of listeners.get(event) || []) {
    try { fn(payload); } catch (e) { console.error('[queue] обработчик упал:', e); }
  }
};

window.addEventListener('online', () => { queueState.online = true; void flush(); });
window.addEventListener('offline', () => { queueState.online = false; });

/* Периодическая попытка: событие online не всегда приходит, если Wi-Fi
   формально подключён, а шлюз недоступен. */
setInterval(() => { if (queueState.items.length && !queueState.blocked) void flush(); }, 10000);
